/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */
import { useSelector } from "react-redux";
import { isEditTaskSelector, tasksSelector } from "../../store/api/tasks/selector";
import { useCreateTaskMutation, useUpdateTaskMutation } from "../../store/api/tasks/taskApiSlice";
import { getAllSiteGroupList } from "../helpers";
import { getDependencyTaskList, prepareTaskPayload } from "./helpers";
import { setBasicDialogOpenFor, setOpenToast } from "../../store/helpers/helperReducers";
import { useStoreDispatch } from "../../store/hooks";
import { setScheduledTask } from "../../store/api/tasks/reducer";
import { IAddScheduledTask } from "../../store/api/tasks/types";
import { useEffect, useState } from "react";
import { IKeyValue } from "../Administrations/types";
import { useTaskDisplay } from "../../pages/Tasks/hooks";
import { useGetAllDevicesByGroupQuery } from "../../store/api/devices/deviceApiSlice";
import { currentSiteGroupsSelector, selectedGeoRegionIdsSelector, selectedGeoRegionSelector, selectedTaskTypeSelector } from "../../store/api/admin/selectors";
import { convertSymbols, errorMessage } from "../../utils/helpers";
import { AppState } from "../../store";
import { IGeoRegion } from "../../store/api/admin/types";
import { useGetGeoRegionsQuery } from "../../store/api/admin/adminApiSlice";
import { LOCAL_STORAGE } from "../../globals/globals";
import { setCurrentSiteGroups } from "../../store/api/admin/reducers";
import { usernameSelector } from "../../store/api/auth/selectors";
import { IServiceDeviceData, ISiteGroup } from "../../store/api/devices/types";
import { encode as base64Encode } from "base-64";
import { IfilterObj } from "../../store/filters/types";

const resolveTaskSubmitErrorMessage = (error: any): string => {
  const normalizeMessage = (message: string): string => convertSymbols(message).trim();

  const backendMessage = typeof error?.data?.message === "string" ? error.data.message.trim() : "";
  if (backendMessage) {
    return normalizeMessage(backendMessage);
  }

  if (typeof error?.data === "string" && error.data.trim()) {
    return normalizeMessage(error.data);
  }

  if (error?.status === "PARSING_ERROR" && typeof error?.data === "string" && error.data.trim()) {
    return normalizeMessage(error.data);
  }

  return errorMessage(error);
};

export const useTaskDisplayData = () => {
  const dispatch = useStoreDispatch();

  const selectedGeoRegion = useSelector<AppState, IGeoRegion>(selectedGeoRegionSelector);
  const selectedGeoRegionIds = useSelector(selectedGeoRegionIdsSelector);
  // Build a filters predicate for geoRegionId IN [all globally selected Region IDs]
  const effectiveRegionIds: string[] =
    selectedGeoRegionIds && selectedGeoRegionIds.length
      ? selectedGeoRegionIds
      : selectedGeoRegion?.objectKey
      ? [selectedGeoRegion.objectKey]
      : [];

  let allDevicesFilterQuery = "";
  if (effectiveRegionIds.length > 0) {
    const paramsString: IfilterObj = { type: "AND", operator: "", fieldName: "", value: "", predicates: [] };
    paramsString.predicates.push({
      type: "",
      operator: "IN",
      fieldName: "geoRegionId",
      value: effectiveRegionIds.join(","),
      predicates: [{}],
    });

    const base64EncodedStr = base64Encode(JSON.stringify(paramsString));
    const searchParams = new URLSearchParams();
    searchParams.append("filters", base64EncodedStr);
    allDevicesFilterQuery = searchParams.toString();
  }

  const { data, refetch } = useGetAllDevicesByGroupQuery({ filters: allDevicesFilterQuery });
  const { data : geoRegions } = useGetGeoRegionsQuery(
    undefined);
  const [createTask, createStatus] = useCreateTaskMutation();
  const [updateTask] = useUpdateTaskMutation();
  const [errMsg, setErrMsg] = useState<IKeyValue>({});

  const rowData = (data || []) as IServiceDeviceData[];
  const deviceList = rowData.map((item: IServiceDeviceData) => ({
    title: item.name || item.ipAddress,
    value: item.objectKey,
  }));
  const scheduledTasks = useTaskDisplay() || [];
  const taskList = scheduledTasks?.data || [];
  const dependencyTaskList = getDependencyTaskList(taskList);
  let tasksFormData: IAddScheduledTask = useSelector(tasksSelector);
  const isEditTask = useSelector(isEditTaskSelector);
  const selectionType = useSelector(selectedTaskTypeSelector);
  const currentSiteGroups = useSelector(currentSiteGroupsSelector);
  const groupList = getAllSiteGroupList(currentSiteGroups) || [];
  const username = useSelector(usernameSelector);


  useEffect(() => {
    refetch();
  }, [refetch]);

  useEffect(() => {
    if (geoRegions && geoRegions.length && geoRegions[0] != null) {
      const updatedGeoRegions = geoRegions ? geoRegions : [];

      // Aggregate Site Groups from all header-selected Regions; if none selected, fall back to current region
      const effectiveRegionIds = (selectedGeoRegionIds && selectedGeoRegionIds.length)
        ? selectedGeoRegionIds
        : (selectedGeoRegion?.objectKey ? [selectedGeoRegion.objectKey] : []);

      const aggregatedSiteGroups: ISiteGroup[] = [];
      effectiveRegionIds.forEach((regionId: string) => {
        const region = updatedGeoRegions.find((rg: IGeoRegion) => rg.objectKey === regionId);
        if (region && region.siteGroups && region.siteGroups.length) {
          region.siteGroups.forEach((sg) => {
            if (!aggregatedSiteGroups.find((existing) => existing.objectKey === sg.objectKey)) {
              aggregatedSiteGroups.push(sg);
            }
          });
        }
      });

      dispatch(setCurrentSiteGroups(aggregatedSiteGroups));

      // Preserve CURRENTREGION for legacy uses (same as before, using single selectedGeoRegion)
      const currentRegionSiteGroups = updatedGeoRegions.find((region: IGeoRegion) => region.objectKey === selectedGeoRegion.objectKey)?.siteGroups || [];
      !!selectedGeoRegion.objectKey && localStorage.setItem(LOCAL_STORAGE.CURRENTREGION, JSON.stringify({ ...selectedGeoRegion, siteGroups: currentRegionSiteGroups }));
    }
  }, [selectedGeoRegion, selectedGeoRegionIds, geoRegions, dispatch]);

  const submitHandler = async () => {
    let taskResponse: any;
    try {
      let geoRegionForTask = selectedGeoRegion?.name;

      if (selectionType === "DEVICE") {
        const selectedDevices = tasksFormData.deviceName || [];
        if (selectedDevices.length > 0) {
          const firstDeviceKey = selectedDevices[0].value;
          const matchingDevice = rowData.find((d: IServiceDeviceData) => d.objectKey === firstDeviceKey);
          if (matchingDevice && matchingDevice.geoRegionName) {
            geoRegionForTask = matchingDevice.geoRegionName;
          }
        }
      } else if (selectionType === "GROUP") {
        const selectedGroups = tasksFormData.siteGroupList || [];
        if (selectedGroups.length > 0 && geoRegions && geoRegions.length > 0) {
          const firstGroupKey = selectedGroups[0].value;
          const matchingSiteGroup = currentSiteGroups.find((sg: ISiteGroup) => sg.objectKey === firstGroupKey);
          if (matchingSiteGroup) {
            const owningRegion = geoRegions.find((rg: IGeoRegion) => rg.objectKey === matchingSiteGroup.parentKey);
            if (owningRegion) {
              geoRegionForTask = owningRegion.name;
            }
          }
        }
      } else if (selectionType === "REGION") {
        const regionList = tasksFormData.regionList || [];
        if (regionList.length === 1) {
          geoRegionForTask = regionList[0]?.title || geoRegionForTask;
        } else if (regionList.length > 1) {
          // For multi-region tasks, treat geoRegion as global (empty)
          geoRegionForTask = "";
        }
      }

      tasksFormData = { ...tasksFormData, geoRegion: geoRegionForTask };
      const payload = prepareTaskPayload(tasksFormData, selectionType, groupList, selectedGeoRegion, username);

      if (payload) {
        if (isEditTask) {
          taskResponse = await updateTask(payload);
        } else {
          taskResponse = await createTask(payload);
        }
      }
      if (taskResponse?.error) throw taskResponse?.error;
      if (taskResponse.data?.name === tasksFormData.name || taskResponse.data?.status === 200) {
        scheduledTasks.refetch();
        dispatch(
          setOpenToast({
            openToast: true,
            toastType: "success",
            message: isEditTask
              ? `The task update "${tasksFormData?.name}" has been initiated.`
              : `The task ${tasksFormData?.name} has been scheduled.`,
          })
        );
        dispatch(setBasicDialogOpenFor(undefined));
        dispatch(setScheduledTask({}));
      }
    } catch (error) {
      dispatch(setOpenToast({ openToast: true, toastType: "error", message: resolveTaskSubmitErrorMessage(error) }));
    }
  };

  return {
    deviceList,
    deviceRowData: rowData,
    dependencyTaskList,
    submitHandler,
    createStatus,
    errMsg,
    setErrMsg,
  };
};
